import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import postsData from "../../posts/posts.json";
import "./Posts.css";

export const Posts = ({ searchActive, onSearchClose }) => {
    const [posts, setPosts] = useState([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        // Filter out posts with future publishAt dates and sort by date, newest first
        const now = new Date();
        const publishedPosts = postsData.filter((post) => {
            if (post.publishAt === null || post.publishAt === undefined) {
                return true;
            }
            return new Date(post.publishAt) <= now;
        });

        const sortedPosts = [...publishedPosts].sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
        });
        setPosts(sortedPosts);
        setFilteredPosts(sortedPosts);
    }, []);

    useEffect(() => {
        if (searchActive) {
            // Focus search input when search is activated
            const searchInput = document.querySelector(".search-input");
            if (searchInput) {
                searchInput.focus();
            }
        }
    }, [searchActive]);

    useEffect(() => {
        if (searchQuery.trim() === "") {
            setFilteredPosts(posts);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = posts.filter(
                (post) =>
                    post.title.toLowerCase().includes(query) ||
                    post.excerpt.toLowerCase().includes(query) ||
                    post.tags.some((tag) => tag.toLowerCase().includes(query)),
            );
            setFilteredPosts(filtered);
        }
    }, [searchQuery, posts]);

    const handleSearchKeyDown = (e) => {
        if (e.key === "Escape") {
            setSearchQuery("");
            onSearchClose();
        }
    };

    return (
        <div className="posts-page">
            <Helmet>
                <title>Posts - mustardtiger.xyz</title>
                <meta
                    name="description"
                    content="Explore posts about FPV, reverse engineering, and embedded systems."
                />

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://mustardtiger.xyz/posts"
                />
                <meta property="og:title" content="Posts - mustardtiger.xyz" />
                <meta
                    property="og:description"
                    content="Explore posts about FPV, reverse engineering, and embedded systems."
                />
                <meta
                    property="og:image"
                    content="https://mustardtiger.xyz/og-image.png"
                />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:url"
                    content="https://mustardtiger.xyz/posts"
                />
                <meta name="twitter:title" content="Posts - mustardtiger.xyz" />
                <meta
                    name="twitter:description"
                    content="Explore posts about FPV, reverse engineering, and embedded systems."
                />
                <meta
                    name="twitter:image"
                    content="https://mustardtiger.xyz/og-image.png"
                />
            </Helmet>
            <div className="posts-header">
                <div className="prompt-line">
                    <span className="prompt">$</span>
                    <span className="command">ls -la posts/</span>
                </div>
                <p className="posts-count">
                    {filteredPosts.length}{" "}
                    {filteredPosts.length === 1 ? "post" : "posts"} found
                </p>
            </div>

            {searchActive && (
                <div className="search-bar">
                    <span className="search-prompt">/</span>
                    <input
                        type="text"
                        className="search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search posts..."
                        autoFocus
                    />
                    <span className="search-hint">ESC to close</span>
                </div>
            )}

            <div className="posts-list">
                {filteredPosts.length === 0 ? (
                    <div className="no-posts">
                        <p>No posts found matching "{searchQuery}"</p>
                    </div>
                ) : (
                    filteredPosts.map((post) => (
                        <Link
                            to={`/posts/${post.id}`}
                            key={post.id}
                            className="post-item"
                        >
                            <div className="post-header">
                                <h2 className="post-title">{post.title}</h2>
                                <span className="post-date">{post.date}</span>
                            </div>
                            <p className="post-excerpt">{post.excerpt}</p>
                            <div className="post-meta">
                                <div className="post-tags">
                                    {post.tags.map((tag) => (
                                        <span key={tag} className="tag">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                                <span className="post-author">
                                    by {post.author}
                                </span>
                            </div>
                        </Link>
                    ))
                )}
            </div>
        </div>
    );
};
